import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
    applyModelCatalog,
    clearLlmCredentials,
    getDefaultModel,
    getDefaultProvider,
    getModelLabel,
    getProviderLabel,
    hasStoredCredentials,
    isValidProviderModelPair,
    listModelsForProvider,
    listProviders,
    loadLlmCredentials,
    saveLlmCredentials,
} from '../../services/llmCredentials';
import { fetchDeepDiveModelCatalog } from '../../services/newsService';

/**
 * BYOK settings surface for the Layer 2 Deep Dive endpoint (W6 / OQ-3).
 *
 * OQ-3 was closed by Trevor 2026-08-06: browser localStorage only. This
 * dialog is the single entry-point for entering / rotating / clearing the
 * user's own LLM provider + model + API key. Values live under the
 * `alphamizer:llm_byok:v1` localStorage entry via `llmCredentials.js`; no
 * server round-trip happens here (BYOK — the key never touches our backend
 * outside of the per-request `runDeepDive` body).
 *
 * Fields:
 *   - Provider select: Anthropic | OpenAI (both allowlisted server-side).
 *   - Model select: fetched from the backend-owned public model catalog.
 *   - API key: password-type input with a show/hide toggle. Values never
 *     appear in error copy, snackbars, or logs.
 *
 * Buttons: Save (validates + writes + fires `onSaved`), Clear key
 * (wipes storage), Cancel (dismiss).
 *
 * The prominent Alert inside the dialog is the spec's fixed notice:
 * "Stored only in this browser. Sent directly to your provider via our
 * server; never saved on our servers."
 */

// Fixed inline notice — the security posture the whole BYOK design hinges on.
// Kept as a module-level constant so tests can assert on it verbatim.
export const LLM_SETTINGS_NOTICE = (
    'Stored only in this browser. Sent directly to your provider via our '
    + 'server; never saved on our servers.'
);

function providerLabel(provider) {
    return getProviderLabel(provider);
}

function modelLabel(model) {
    return getModelLabel(model);
}

export default function LlmSettingsDialog({
    open,
    onClose,
    onSaved = null,
    initialProvider = null,
    initialModel = null,
    reason = null,
}) {
    const [provider, setProvider] = useState('');
    const [model, setModel] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [showKey, setShowKey] = useState(false);
    const [error, setError] = useState('');
    const [hasStored, setHasStored] = useState(false);
    const [catalogRevision, setCatalogRevision] = useState(0);
    const [catalogLoading, setCatalogLoading] = useState(false);

    // Whenever the dialog opens, hydrate the form from whatever is currently
    // in storage so the user sees the state they last saved (with a masked
    // key). If nothing is stored yet, fall back to initialProvider/model
    // hints so the auto-open-from-DeepDiveDialog path can pre-select the
    // provider the caller expected.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;

        function hydrateForm() {
            setError('');
            setShowKey(false);
            const providers = listProviders();
            const stored = loadLlmCredentials();
            if (stored) {
                const storedProvider = providers.includes(stored.provider)
                    ? stored.provider
                    : getDefaultProvider();
                const storedModel = isValidProviderModelPair(
                    storedProvider,
                    stored.model,
                )
                    ? stored.model
                    : getDefaultModel(storedProvider);
                setProvider(storedProvider);
                setModel(storedModel);
                setApiKey(stored.api_key);
                setHasStored(true);
                return;
            }
            setHasStored(false);
            const seededProvider = initialProvider && providers.includes(initialProvider)
                ? initialProvider
                : getDefaultProvider();
            setProvider(seededProvider);
            const modelsForProvider = listModelsForProvider(seededProvider);
            const seededModel = initialModel && modelsForProvider.includes(initialModel)
                ? initialModel
                : getDefaultModel(seededProvider);
            setModel(seededModel);
            setApiKey('');
        }

        async function hydrate() {
            // Seed the form immediately from the bundled fallback, then let
            // the backend catalog replace it. This avoids an empty settings
            // surface while keeping the backend authoritative.
            hydrateForm();
            setCatalogLoading(true);
            try {
                const catalog = await fetchDeepDiveModelCatalog();
                applyModelCatalog(catalog);
                if (!cancelled) {
                    setCatalogRevision((value) => value + 1);
                    hydrateForm();
                }
            } catch (_err) {
                // The built-in fallback remains usable; the backend still
                // validates the selected model when Deep Dive is submitted.
            } finally {
                if (!cancelled) setCatalogLoading(false);
            }
        }

        hydrate();
        return () => {
            cancelled = true;
        };
    }, [open, initialProvider, initialModel]);

    const providers = useMemo(() => listProviders(), [catalogRevision]);

    const modelsForCurrentProvider = useMemo(
        () => listModelsForProvider(provider),
        [provider, catalogRevision],
    );

    const handleProviderChange = useCallback((event) => {
        const next = String(event.target.value || '').toLowerCase();
        setProvider(next);
        // Reset the model when the provider changes; the previous selection
        // may not belong to the new provider's allowlist.
        const nextModels = listModelsForProvider(next);
        setModel(nextModels[0] || '');
        setError('');
    }, []);

    const handleModelChange = useCallback((event) => {
        setModel(String(event.target.value || ''));
        setError('');
    }, []);

    const handleKeyChange = useCallback((event) => {
        setApiKey(String(event.target.value || ''));
        setError('');
    }, []);

    const toggleShowKey = useCallback(() => setShowKey((current) => !current), []);

    const handleSave = useCallback(() => {
        setError('');
        if (!isValidProviderModelPair(provider, model)) {
            setError('Please pick a provider and a supported model.');
            return;
        }
        if (!apiKey || apiKey.trim() === '') {
            setError('API key must not be empty.');
            return;
        }
        try {
            saveLlmCredentials({ provider, model, api_key: apiKey });
        } catch (err) {
            // Never surface the raw message from `saveLlmCredentials` if it
            // could echo the key — the current impl doesn't, but this stays
            // defensive against future edits.
            setError('Could not save credentials in this browser.');
            return;
        }
        setHasStored(true);
        if (typeof onSaved === 'function') {
            onSaved({ provider, model });
        }
        if (typeof onClose === 'function') {
            onClose();
        }
    }, [provider, model, apiKey, onSaved, onClose]);

    const handleSubmit = useCallback((event) => {
        event.preventDefault();
        if (!catalogLoading) handleSave();
    }, [catalogLoading, handleSave]);

    const handleClear = useCallback(() => {
        setError('');
        clearLlmCredentials();
        setApiKey('');
        setHasStored(hasStoredCredentials());
    }, []);

    const handleCancel = useCallback(() => {
        setError('');
        if (typeof onClose === 'function') onClose();
    }, [onClose]);

    return (
        <Dialog
            open={Boolean(open)}
            onClose={handleCancel}
            fullWidth
            maxWidth="xs"
            data-testid="llm-settings-dialog"
            PaperProps={{
                component: 'form',
                autoComplete: 'off',
                onSubmit: handleSubmit,
                sx: {
                    borderRadius: 2,
                    backgroundImage: 'none',
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    px: 2.5,
                    pt: 2,
                    pb: 0.5,
                }}
            >
                AI Deep Dive credentials
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pt: 1.5, pb: 1.5 }}>
                {reason && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                        data-testid="llm-settings-reason"
                    >
                        {reason}
                    </Alert>
                )}
                <Alert severity="info" sx={{ mb: 2 }} data-testid="llm-settings-notice">
                    {LLM_SETTINGS_NOTICE}
                </Alert>
                <Box sx={{ display: 'grid', gap: 1.75 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="llm-provider-select-label">Provider</InputLabel>
                        <Select
                            labelId="llm-provider-select-label"
                            label="Provider"
                            value={provider}
                            onChange={handleProviderChange}
                            inputProps={{ 'data-testid': 'llm-provider-select' }}
                            disabled={catalogLoading}
                        >
                            {providers.map((prov) => (
                                <MenuItem key={prov} value={prov}>
                                    {providerLabel(prov)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel id="llm-model-select-label">Model</InputLabel>
                        <Select
                            labelId="llm-model-select-label"
                            label="Model"
                            value={model}
                            onChange={handleModelChange}
                            inputProps={{ 'data-testid': 'llm-model-select' }}
                            disabled={catalogLoading || modelsForCurrentProvider.length === 0}
                        >
                            {modelsForCurrentProvider.map((m) => (
                                <MenuItem key={m} value={m}>
                                    {modelLabel(m)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        size="small"
                        label="API key"
                        type={showKey ? 'text' : 'password'}
                        value={apiKey}
                        onChange={handleKeyChange}
                        autoComplete="off"
                        // aria-owns / spellCheck defenses to keep the browser
                        // from ever autofilling / prompting-save-password on
                        // this input. Not security-critical, just hygiene.
                        spellCheck={false}
                        inputProps={{
                            'data-testid': 'llm-api-key-input',
                            'aria-label': 'API key',
                            autoCapitalize: 'off',
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        type="button"
                                        aria-label={showKey ? 'Hide API key' : 'Show API key'}
                                        onClick={toggleShowKey}
                                        edge="end"
                                        size="small"
                                        data-testid="llm-api-key-visibility-toggle"
                                    >
                                        {showKey ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {error && (
                        <Typography
                            variant="caption"
                            color="error.main"
                            sx={{ fontSize: '0.72rem' }}
                            data-testid="llm-settings-error"
                        >
                            {error}
                        </Typography>
                    )}
                    {hasStored && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem' }}
                            data-testid="llm-settings-stored-hint"
                        >
                            A key is currently stored in this browser. Clear it below to
                            remove it, or overwrite it with a new value and Save.
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2, pt: 0.5, gap: 1 }}>
                <Button
                    type="button"
                    onClick={handleClear}
                    color="inherit"
                    size="small"
                    disabled={!hasStored && !apiKey}
                    data-testid="llm-settings-clear-button"
                    sx={{ textTransform: 'none' }}
                >
                    Clear key
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                    type="button"
                    onClick={handleCancel}
                    color="inherit"
                    size="small"
                    data-testid="llm-settings-cancel-button"
                    sx={{ textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    disabled={catalogLoading}
                    data-testid="llm-settings-save-button"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

LlmSettingsDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func,
    initialProvider: PropTypes.string,
    initialModel: PropTypes.string,
    /** Why the user was bounced here, e.g. a provider 401. Rendered as a
     * persistent warning above the standard notice, because the transient
     * snackbar that raised it is gone by the time this dialog has focus. */
    reason: PropTypes.string,
};
